import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";
import { useQueryClient } from "@tanstack/react-query";

export interface IMenuListParams {
  page_size?: number;
  page?: number;
  search?: string;
  order_by?: string;
  device_id?: number;
  active?: boolean;
}

export interface IMenu {
  description: string;
  number_of_devices: number;
  name: string;
  number_of_categories: number;
  number_of_accounts: number;
  id: ID;
  active: boolean;
  owned_by_organisation: boolean;
  order: number;
}

export interface IMenuCreateParams {
  name: string;
  description: string;
  active: boolean;
}

export interface IMenuCreateResponse {
  name: string;
  active: boolean;
  description: string;
  order: number;
  id: number;
}

export interface ICreateRelationshipParamsBase {
  menu_id: ID;
}

export interface ICreateDeviceMenuRelationshipParams
  extends ICreateRelationshipParamsBase {
  device_id: number;
  is_default_menu: boolean;
}

export interface ICreateCategoryMenuRelationshipParams
  extends ICreateRelationshipParamsBase {
  menu_category_id: number;
}

export interface ICreateAccountMenuRelationshipParams
  extends ICreateRelationshipParamsBase {
  organisation_id: number;
}

export interface ICreateRelationshipResponseBase {
  menu_id: number;
  id: number;
}

export interface ICreateDeviceMenuRelationshipResponse
  extends ICreateRelationshipResponseBase {
  device_id: number;
  is_default_menu: boolean;
}

export interface ICreateCategoryMenuRelationshipResponse
  extends ICreateRelationshipResponseBase {
  menu_category_id: number;
}

export interface ICreateAccountMenuRelationshipResponse
  extends ICreateRelationshipResponseBase {
  menu_account_id: number;
}

export interface IMenuListResponse extends APIPaginatedResponse<IMenu> {}

export interface IMenuDeviceRelationship {
  device_id: number;
  is_default_menu: boolean;
  id: number;
  device: {
    name: string;
    updated_at: string;
    status: Array<unknown>;
    id: number;
    accessories: Array<unknown>;
    created_at: string;
  };
  menu: {
    number_of_devices: number | null;
    name: string;
    active: boolean;
    id: number;
    owned_by_organisation: boolean;
    order: number;
    description: string;
    number_of_categories: number | null;
  };
  menu_id: number;
}

export interface IMenuDeviceRelationshipListParams {
  menu_id: ID;
}

export interface IMenuDeviceRelationshipListResponse
  extends APIPaginatedResponse<IMenuDeviceRelationship> {}

export interface IMenuCategoriesRelationshipListParams {
  menu_id: ID;
}

export interface IMenuCategoriesRelationship {
  id: number;
  menu_category_id: number;
  menu: {
    id: number;
    owned_by_organisation: boolean;
    number_of_categories: number | null;
    description: string;
    number_of_devices: number | null;
    name: string;
    order: number;
    active: boolean;
  };
  menu_category: {
    id: number;
    updated_at: string;
    number_of_items: number | null;
    visibility: number;
    custom_printer_configurations: Array<unknown>;
    description: string;
    has_time_restrictions: boolean | null;
    created_at: string;
    theme_color: string | null;
    name: string;
    order: number;
    active: boolean;
  };
  menu_id: number;
  order: number;
}

export interface IMenuAccountsRelationship {
  restaurant: {
    id: ID;
    name: string;
  };
  id: ID;
}

export interface IMenuAccountsRelationshipListParams {
  menu_id: ID;
}

export interface IMenuAccountsRelationshipListResponse
  extends APIPaginatedResponse<IMenuAccountsRelationship> {}

export interface IMenuUpdateParams extends IMenuCreateParams {
  id: ID;
}

export interface IUpdateCategoryMenuRelationshipParams
  extends ICreateCategoryMenuRelationshipParams {
  relationship_id: ID;
}

export interface IUpdateDeviceMenuRelationshipParams
  extends ICreateDeviceMenuRelationshipParams {
  relationship_id: ID;
}

export interface IDeleteRelationshipParamsBase {
  menu_id: ID;
  relationship_id: ID;
}

export interface IDeleteDeviceMenuRelationshipParams {
  menu_id;
}

export interface IMenusService {
  list: (params: IMenuListParams) => UseQueryResult<IMenuListResponse, Error>;
  create: () => UseMutationResult<
    IMenuCreateResponse,
    Error,
    IMenuCreateParams,
    unknown
  >;
  update: () => UseMutationResult<
    IMenuCreateResponse,
    Error,
    IMenuUpdateParams,
    unknown
  >;
  retrieve: (id: ID) => UseQueryResult<IMenu, Error>;
  delete: () => UseMutationResult<
    boolean,
    Error,
    IDeleteDeviceMenuRelationshipParams,
    unknown
  >;

  // List Relationships
  listDeviceRelationships: (
    params: IMenuDeviceRelationshipListParams,
  ) => UseQueryResult<IMenuDeviceRelationshipListResponse, Error>;

  listCategoriesRelationships: (
    params: IMenuCategoriesRelationshipListParams,
  ) => UseQueryResult<IMenuCategoriesRelationship[], Error>;

  listAccountsRelationships: (
    params: IMenuAccountsRelationshipListParams,
  ) => UseQueryResult<IMenuAccountsRelationshipListResponse, Error>;

  // Create Relationships
  createDeviceMenuRelationship: () => UseMutationResult<
    ICreateDeviceMenuRelationshipResponse,
    Error,
    ICreateDeviceMenuRelationshipParams,
    unknown
  >;
  createCategoryMenuRelationship: () => UseMutationResult<
    ICreateCategoryMenuRelationshipResponse,
    Error,
    ICreateCategoryMenuRelationshipParams,
    unknown
  >;
  createAccountMenuRelationship: () => UseMutationResult<
    ICreateAccountMenuRelationshipResponse,
    Error,
    ICreateAccountMenuRelationshipParams,
    unknown
  >;

  // Update Relationships
  updateCategoryMenuRelationship: () => UseMutationResult<
    ICreateCategoryMenuRelationshipResponse,
    Error,
    IUpdateCategoryMenuRelationshipParams,
    unknown
  >;
  updateDeviceMenuRelationship: () => UseMutationResult<
    ICreateDeviceMenuRelationshipResponse,
    Error,
    IUpdateDeviceMenuRelationshipParams,
    unknown
  >;

  // Delete Relationships
  deleteCategoryMenuRelationship: () => UseMutationResult<
    void,
    Error,
    IDeleteRelationshipParamsBase,
    unknown
  >;
  deleteDeviceMenuRelationship: () => UseMutationResult<
    void,
    Error,
    IDeleteRelationshipParamsBase,
    unknown
  >;
  deleteAccountMenuRelationship: () => UseMutationResult<
    void,
    Error,
    IDeleteRelationshipParamsBase,
    unknown
  >;
}

export class MenusService implements IMenusService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(params: IMenuListParams): UseQueryResult<IMenuListResponse, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== null && value !== undefined,
      ),
    );

    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "menus"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<IMenuListResponse>>(
          "/menu/me/menus",
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: updatedParams,
          },
        );

        return {
          results: data.results,
          count: data.count,
          next: data.next,
          previous: data.previous,
        };
      },
    });
  }

  create(): UseMutationResult<IMenuCreateResponse, Error, IMenuCreateParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "menus", "create"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<
          AxiosResponse<IMenuCreateResponse>
        >("/menu/me/menus", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: params,
        });

        return {
          name: data.name,
          active: data.active,
          description: data.description,
          order: data.order,
          id: data.id,
        };
      },

      onMutate: async (newMenu) => {},
      onError: (err, newMenu, context) => {},
      onSettled: () => {},
    });
  }

  update(): UseMutationResult<IMenuCreateResponse, Error, IMenuUpdateParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "menus", "update"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, ...restParams } = params;
        const { data } = await this.api.patch<
          AxiosResponse<IMenuCreateResponse>
        >(`/menu/me/menus/${params.id}`, {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: restParams,
        });

        return data;
      },
      onMutate: async (newMenu) => {},
      onError: (err, newMenu, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            "operations",
            "menus",
            "retrieve",
            variables.id.toString(),
          ],
        });
      },
    });
  }

  retrieve(id: ID): UseQueryResult<IMenu, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "menus", "retrieve", id],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<IMenu>>(
          `/menu/me/menus/${id}`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: {},
          },
        );
        return data;
      },
    });
  }

  delete(): UseMutationResult<
    boolean,
    Error,
    IDeleteDeviceMenuRelationshipParams,
    unknown
  > {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "menus", "delete"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete<AxiosResponse<boolean>>(
          `/menu/me/menus/${params.menu_id}`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return true;
      },
      onError: (err, variables, context) => {
        return false;
      },
      onSettled: (data, error, variables) => {},
    });
  }

  // List Relationships

  listDeviceRelationships(
    params: IMenuDeviceRelationshipListParams,
  ): UseQueryResult<IMenuDeviceRelationshipListResponse, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: [
          "operations",
          "menus",
          "listDeviceRelationships",
          params.menu_id,
        ],
        venueID: this.api.selectedVenue,
        params: {
          menu_id: params.menu_id,
        },
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IMenuDeviceRelationshipListResponse>
        >("/menu/me/menus/menu_devices/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: {
            menu_id: params.menu_id,
            page_size: 100,
          },
        });
        return data;
      },
    });
  }

  listCategoriesRelationships(
    params: IMenuCategoriesRelationshipListParams,
  ): UseQueryResult<IMenuCategoriesRelationship[], Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: [
          "operations",
          "menus",
          "listCategoriesRelationships",
          params.menu_id,
        ],
        venueID: this.api.selectedVenue,
        params: {
          menu_id: params.menu_id,
        },
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IMenuCategoriesRelationship[]>
        >("/menu/me/menucategory_menus/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: {
            menu_id: params.menu_id,
          },
        });
        return data;
      },
    });
  }

  listAccountsRelationships(
    params: IMenuAccountsRelationshipListParams,
  ): UseQueryResult<IMenuAccountsRelationshipListResponse, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: [
          "operations",
          "menus",
          "listAccountsRelationships",
          params.menu_id,
        ],
        venueID: this.api.selectedVenue,
        params: {
          menu_id: params.menu_id,
        },
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IMenuAccountsRelationshipListResponse>
        >(`/menu/me/menus/${params.menu_id}/shared/`, {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: {
            page_size: 100,
          },
        });
        return data;
      },
    });
  }

  // Create Relationships

  createDeviceMenuRelationship(): UseMutationResult<
    ICreateDeviceMenuRelationshipResponse,
    Error,
    ICreateDeviceMenuRelationshipParams
  > {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "menus", "createDeviceMenuRelationship"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<
          AxiosResponse<ICreateDeviceMenuRelationshipResponse>
        >("/menu/me/menus/menu_devices", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params,
        });
        return data;
      },
      // TODO: Implement Functionality when doing edit menu workflow
      onMutate: async (newDeviceMenuRelationship) => {},
      onError: (err, newDeviceMenuRelationship, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            "operations",
            "menus",
            "listDeviceRelationships",
            variables.menu_id.toString(),
          ],
        });
      },
    });
  }

  createCategoryMenuRelationship(): UseMutationResult<
    ICreateCategoryMenuRelationshipResponse,
    Error,
    ICreateCategoryMenuRelationshipParams
  > {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "menus", "createCategoryMenuRelationship"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<
          AxiosResponse<ICreateCategoryMenuRelationshipResponse>
        >("/menu/me/menucategory_menus", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params,
        });
        return data;
      },
      // TODO: Implement Functionality when doing edit menu workflow
      onMutate: async (newCategoryMenuRelationship) => {},
      onError: (err, newCategoryMenuRelationship, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            "operations",
            "menus",
            "listCategoriesRelationships",
            variables.menu_id.toString(),
          ],
        });
      },
    });
  }

  createAccountMenuRelationship(): UseMutationResult<
    ICreateAccountMenuRelationshipResponse,
    Error,
    ICreateAccountMenuRelationshipParams
  > {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "menus", "createAccountMenuRelationship"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<
          AxiosResponse<ICreateAccountMenuRelationshipResponse>
        >("/menu/me/menus/shared/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params,
        });
        return data;
      },
      onMutate: async (newAccountMenuRelationship) => {},
      onError: (err, newAccountMenuRelationship, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            "operations",
            "menus",
            "listAccountsRelationships",
            variables.menu_id.toString(),
          ],
        });
      },
    });
  }

  // Update Relationships

  updateCategoryMenuRelationship(): UseMutationResult<
    ICreateCategoryMenuRelationshipResponse,
    Error,
    IUpdateCategoryMenuRelationshipParams,
    unknown
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "menus", "updateCategoryMenuRelationship"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { relationship_id, ...updatedParams } = params;

        const { data } = await this.api.patch<
          AxiosResponse<ICreateCategoryMenuRelationshipResponse>
        >(`/menu/me/menucategory_menus/${relationship_id}`, {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: updatedParams,
        });
        return data;
      },
      onMutate: async (newCategoryMenuRelationship) => {},
      onError: (err, newCategoryMenuRelationship, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            "operations",
            "menus",
            "listCategoriesRelationships",
            variables.menu_id.toString(),
          ],
        });
      },
    });
  }

  updateDeviceMenuRelationship(): UseMutationResult<
    ICreateDeviceMenuRelationshipResponse,
    Error,
    IUpdateDeviceMenuRelationshipParams,
    unknown
  > {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "menus", "updateDeviceMenuRelationship"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { relationship_id, ...updatedParams } = params;

        const { data } = await this.api.patch<
          AxiosResponse<ICreateDeviceMenuRelationshipResponse>
        >(`/menu/me/menus/menu_devices/${relationship_id}`, {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: updatedParams,
        });
        return data;
      },
      onMutate: async (newDeviceMenuRelationship) => {},
      onError: (err, newDeviceMenuRelationship, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            "operations",
            "menus",
            "listDeviceRelationships",
            variables.menu_id.toString(),
          ],
        });
      },
    });
  }

  // Delete Relationships

  deleteCategoryMenuRelationship(): UseMutationResult<
    void,
    Error,
    IDeleteRelationshipParamsBase,
    unknown
  > {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "menus", "deleteCategoryMenuRelationship"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete(
          `/menu/me/menucategory_menus/${params.relationship_id}`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
      },
      onMutate: async (deletedCategoryMenuRelationship) => {},
      onError: (err, deletedCategoryMenuRelationship, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            "operations",
            "menus",
            "listCategoriesRelationships",
            variables.menu_id.toString(),
          ],
        });
      },
    });
  }

  deleteDeviceMenuRelationship(): UseMutationResult<
    void,
    Error,
    IDeleteRelationshipParamsBase,
    unknown
  > {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "menus", "deleteDeviceMenuRelationship"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete(
          `/menu/me/menus/menu_devices/${params.relationship_id}`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
      },
      onMutate: async (deletedDeviceMenuRelationship) => {},
      onError: (err, deletedDeviceMenuRelationship, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            "operations",
            "menus",
            "listDeviceRelationships",
            variables.menu_id.toString(),
          ],
        });
      },
    });
  }

  deleteAccountMenuRelationship(): UseMutationResult<
    void,
    Error,
    IDeleteRelationshipParamsBase,
    unknown
  > {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "menus", "deleteAccountMenuRelationship"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete(
          `/menu/me/menus/shared/${params.relationship_id}`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
      },
      onMutate: async (deletedAccountMenuRelationship) => {},
      onError: (err, deletedAccountMenuRelationship, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            "operations",
            "menus",
            "listAccountsRelationships",
            variables.menu_id.toString(),
          ],
        });
      },
    });
  }
}
